import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { CouponTypeEnum } from '@/api/affiliate/types/Coupon'
import { useCurrentUser } from '@/api/auth/queries/useCurrentUser'
import { useCurrencyRates } from '@/api/currency/queries/useCurrencyRates'
import { usePackageFeesFromRequests } from '@/api/package-req/hooks/usePackageFeesFromRequests'
import { useCreatePaymentLink } from '@/api/payments/mutations/useCreatePaymentLink'
import { ID_REQUIRED_THRESHOLD, TAX_FREE_THRESHOLD } from '@/api/payments/paymentsService'
import { useGetPackageTaxes } from '@/api/payments/queries/useGetPackageTaxes'
import { CheckCouponResponseData } from '@/api/payments/types/CheckCouponResponse'
import { CreatePaymentLinkPayload } from '@/api/payments/types/CreatePaymentLinkPayload'
import { UserPkg } from '@/api/user-package/types/UserPkg'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import AttentionIcon from '@/components/icons/attention.svg?react'
import { TextField } from '@/components/TextField'
import { Button } from '@/components/ui/button'
import { isValidIsraeliId } from '@/helpers/israeliId'
import { ShippingPrices } from '@/helpers/priceChart'
import { RoutePath } from '@/RoutePath'

import { PkgPreview } from '../ClientPkg/components/PkgPreview'
import { CouponInput } from './components/CouponInput'
import { PaymentData } from './components/PaymentData'
import { PaymentOptions } from './components/PaymentOptions'
import { PaymentSummaryDetails } from './components/PaymentSummaryDetails'
import { ShippingOptions } from './ShippingPage'

interface Props {
  pkg: UserPkg
  shippingOptions: ShippingOptions
  paymentData: PaymentData
  onUpdatePaymentData: (data: Partial<PaymentData>) => void
  shippingPrices: ShippingPrices
  insurancePrice: number
  pkgValue: number
}

export default function SummaryPage({
  pkg,
  shippingOptions,
  paymentData,
  onUpdatePaymentData,
  shippingPrices,
  insurancePrice,
  pkgValue,
}: Props) {
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const navigate = useNavigate()
  const { data: currencyRates } = useCurrencyRates()
  const poalimRate = currencyRates?.poalimRate ?? 0
  const BOIrate = currencyRates?.BOIrate ?? 0
  const { data: loggedUser } = useCurrentUser()
  const { isPending: isCreatingPayment, mutateAsync: createPaymentLink } = useCreatePaymentLink()
  const { data: packageFees, isLoading: isLoadingPackageFees } = usePackageFeesFromRequests(pkg.id)
  const shouldFetchTaxes = pkgValue >= TAX_FREE_THRESHOLD
  const { data: taxesData, isLoading: isPendingTaxes } = useGetPackageTaxes(pkg?.id, shouldFetchTaxes)
  const isLoadingTaxes = shouldFetchTaxes && isPendingTaxes
  const israeliIdRef = useRef<HTMLInputElement>(null)

  const shouldCollectIsraeliId = pkgValue > ID_REQUIRED_THRESHOLD
  const couponDiscountAmount = useMemo(() => {
    if (!paymentData.coupon) return 0

    const coupon = paymentData.coupon
    if (coupon.shippingMethod && coupon.shippingMethod !== 'all' && coupon.shippingMethod !== shippingOptions.selectedShippingMethod) {
      return 0
    }
    switch (coupon.type) {
      case CouponTypeEnum.Percentage:
        return parseFloat(((shippingPrices[shippingOptions.selectedShippingMethod] ?? 0) * (+coupon.value / 100)).toFixed(2))
      case CouponTypeEnum.Fixed:
        return +coupon.value
      case CouponTypeEnum.MktUniteFee:
      case CouponTypeEnum.WarehouseFees:
        return paymentData.feesDiscount
    }
  }, [paymentData.coupon, shippingOptions.selectedShippingMethod, shippingPrices, paymentData.feesDiscount])

  const totalPrice = useMemo(() => {
    const shippingPrice = shippingPrices[shippingOptions.selectedShippingMethod]
    if (!shippingPrice) return 0

    const res =
      (shippingPrice ?? 0) +
      (packageFees?.uniteFee ?? 0) +
      (pkg.delayChargePrice ?? 0) +
      (pkg.taxFreeFeeUSD ?? 0) +
      (packageFees?.shrinkRequestFeeUSD ?? 0) +
      (packageFees?.photoRequestFeeUSD ?? 0) +
      (shippingOptions.isInsuranceAdded ? insurancePrice : 0) +
      (taxesData?.sum ?? 0) -
      couponDiscountAmount

    return parseFloat(res.toFixed(2))
  }, [
    shippingPrices,
    shippingOptions.selectedShippingMethod,
    shippingOptions.isInsuranceAdded,
    packageFees?.uniteFee,
    packageFees?.shrinkRequestFeeUSD,
    packageFees?.photoRequestFeeUSD,
    pkg.delayChargePrice,
    pkg.taxFreeFeeUSD,
    insurancePrice,
    taxesData?.sum,
    couponDiscountAmount,
  ])

  const handlePayment = async () => {
    if (shouldCollectIsraeliId && !isValidIsraeliId(paymentData.israeliId)) {
      setErrorMessage('מספר תעודת הזהות לא תקין.')
      setShowErrorModal(true)
      israeliIdRef.current?.focus()
      return
    }

    try {
      const payload: CreatePaymentLinkPayload = {
        email: loggedUser!.UserAttributes.email || '',
        fullName: loggedUser!.UserAttributes.name + ' ' + loggedUser!.UserAttributes.family_name,
        phone: (loggedUser!.UserAttributes.phone_number || '').split('+972')[1] || '',
        pid: pkg.id,
        sm: shippingOptions.selectedShippingMethod,
        shippingAddressId: shippingOptions.selectedAddressId,
        contentVal: pkgValue.toString(),
        couponDiscount: paymentData.feesDiscount,
        couponCode: paymentData.couponCode,
        contents: pkg.contents,
        paymentMethod: paymentData.selectedPaymentMethod,
        country: pkg.warehouse,
        costumerIsraeliId: paymentData.israeliId,
      }

      if (shippingOptions.isInsuranceAdded) {
        payload.insurance = insurancePrice
      }

      if (taxesData?.sum) {
        payload.taxesToPay = taxesData.sum * poalimRate
      }

      const url = await createPaymentLink(payload)
      // console.log('🚀 ~ handlePayment ~ url:', url)
      onUpdatePaymentData({ paymentUrl: url, totalPrice })
      if (paymentData.selectedPaymentMethod == 'apple') {
        window.location.href = url
      } else {
        navigate(`${RoutePath.PaymentProcess}/${pkg.id}`)
      }
    } catch (error) {
      console.log('Payment error:', error)
      setErrorMessage('תקלה בשרת! אנא נסה שנית או פנה לשירות הלקוחות')
      setShowErrorModal(true)
    }
  }

  const handleCouponApply = (couponData: CheckCouponResponseData) => {
    onUpdatePaymentData({
      coupon: couponData.coupon,
      feesDiscount: couponData.feesDiscount,
      couponCode: couponData.coupon.code,
    })
  }

  const getShippingPriceInILS = () => {
    const priceInUSD = parseFloat(String(shippingPrices[shippingOptions.selectedShippingMethod]))
    const totalPriceInILS = priceInUSD * 0.9 * 1.03 * poalimRate + priceInUSD * 0.1 * BOIrate
    return totalPriceInILS
  }

  const isContinueDisabled = useMemo(() => {
    return isCreatingPayment || isLoadingTaxes || isLoadingPackageFees || !shippingOptions.selectedShippingMethod
  }, [isCreatingPayment, isLoadingTaxes, isLoadingPackageFees, shippingOptions.selectedShippingMethod])

  return (
    <div className='space-y-6'>
      {/* Title Section */}
      <div>
        <h1 className='text-text-primary mb-2 text-2xl font-medium'>סיכום ואישור תשלום</h1>
        <p className='text-text-secondary text-sm leading-relaxed'>עברנו כמעט הכל! נא לבדוק שהפרטים נכונים לפני התשלום</p>
      </div>

      {/* Package Card */}
      <PkgPreview pkg={pkg} currentView='all' hideActions hideStatus />

      {/* Payment Method Selection */}
      <PaymentOptions
        selectedPaymentMethod={paymentData.selectedPaymentMethod}
        onSelectPaymentMethod={method => onUpdatePaymentData({ selectedPaymentMethod: method })}
      />
      <PaymentSummaryDetails
        couponDiscountAmount={couponDiscountAmount}
        delayChargePrice={pkg.delayChargePrice ?? 0}
        taxFreeFeeUSD={pkg.taxFreeFeeUSD ?? 0}
        insurancePrice={insurancePrice}
        taxesData={taxesData}
        packageFees={packageFees}
        paymentData={paymentData}
        shippingOptions={shippingOptions}
        shippingPrices={shippingPrices}
        isLoadingTaxes={isLoadingTaxes}
        totalPrice={totalPrice}
      />

      {shouldCollectIsraeliId && (
        <div className='border-border-default space-y-2 rounded-lg border bg-white p-3'>
          <h3 className='text-text-primary text-sm font-medium'>
            מכיוון ששווי החבילה מעל ${ID_REQUIRED_THRESHOLD} יש צורך גם בתעודת זהות:
          </h3>
          <TextField
            ref={israeliIdRef}
            placeholder='ת.ז.'
            inputMode='numeric'
            value={paymentData.israeliId}
            onChange={e => onUpdatePaymentData({ israeliId: e.target.value })}
            className='w-full'
          />
        </div>
      )}
      {/* Taxes Info */}
      {pkgValue > 0 && (
        <div className='rounded-lg bg-blue-50 p-4'>
          {pkgValue < TAX_FREE_THRESHOLD && <div className='text-sm'>איזה כיף! החבילה בשווי נמוך מ-150$ ולכן פטורה ממיסים.</div>}
          {pkgValue >= TAX_FREE_THRESHOLD && (
            <div className='text-sm'>* טיפ לפעם הבאה: לפעמים זול יותר לפצל הזמנה גדולה לכמה הזמנות מהספק כדי לחסוך בעלויות המיסים 😎</div>
          )}
        </div>
      )}
      {/* Coupon Input */}
      <div className='space-y-4'>
        <h3 className='text-text-primary text-sm font-medium'>קוד קופון</h3>
        <CouponInput
          pkg={pkg}
          uniteFee={packageFees?.uniteFee ?? 0}
          priceInILS={getShippingPriceInILS()}
          shippingMethod={shippingOptions.selectedShippingMethod}
          coupon={paymentData.coupon}
          onApplyCoupon={handleCouponApply}
          onError={msg => {
            setErrorMessage(msg)
            setShowErrorModal(true)
          }}
        />
      </div>

      {/* Payment Button */}
      <div className='flex justify-center'>
        <Button
          loading={isCreatingPayment}
          onClick={handlePayment}
          disabled={isContinueDisabled}
          className='w-full max-w-[335px]'
          size='lg'
        >
          המשך לתשלום
        </Button>
      </div>

      {/* Error Modal */}
      <ConfirmDialog
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        onConfirm={() => setShowErrorModal(false)}
        confirmText='סגור'
        Icon={AttentionIcon}
        title='שים לב!'
        content={errorMessage}
      />
    </div>
  )
}
